/**
 * A Test request that never reached a verdict, described as a `WarehouseProblem` (ACT2-1077).
 *
 * The server describes every failure it sees. What it cannot describe is the request itself failing:
 * the network dropped, the server erred, or the procedure refused its input before testing anything.
 * Those are the only words written in the browser, and the transport's own text is kept as the detail.
 * `listRequestFailed` is the same for a list that failed to load, where nothing was tested.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';

import { orpcErrorDetail } from '@/_shared/lib/orpc-errors';

import { isPermissionRefusal } from '../accountCreatePermission';
import { refusalMessage } from '../refusalCopy';

function serverProblem(err: unknown): WarehouseProblem | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const data = (err as { data?: unknown }).data;
  const problem = data && typeof data === 'object' ? (data as { problem?: unknown }).problem : undefined;
  return problem && typeof problem === 'object' ? (problem as WarehouseProblem) : undefined;
}

/** The server answered: an oRPC error, which carries a `code`. */
function serverCode(err: unknown): string | undefined {
  const code = err instanceof Error ? (err as { code?: unknown }).code : undefined;
  return typeof code === 'string' ? code : undefined;
}

function detailOf(err: unknown): { details?: string } {
  const text = orpcErrorDetail(err) ?? (err instanceof Error ? err.message.trim() : '');
  return text ? { details: text } : {};
}

/**
 * The request never reached the server: `fetch` rejects with a TypeError ("Failed to fetch", "Load
 * failed"). An error with no `code` that is not one — a proxy's 502 page the client could not parse as
 * an oRPC error — did reach something, so it is not a network problem.
 */
function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError;
}

/** Sign-in and permission refusals: waiting will not help, signing in or an admin will. */
const ACCESS_CODES: ReadonlySet<string> = new Set(['UNAUTHORIZED', 'FORBIDDEN']);
const ACCESS_STEP = 'Sign in again, or ask a workspace admin for access.';

export function testRequestFailed(err: unknown): WarehouseProblem {
  const fromServer = serverProblem(err);
  if (fromServer) return fromServer;
  // RudderStack did not answer the permission check: an outage, not the user's access.
  if ((err as { data?: { reason?: unknown } } | null)?.data?.reason === 'rudderstack_permission_unknown') {
    return {
      kind: 'unknown',
      message: 'We could not check your RudderStack permission.',
      nextStep: 'Wait a moment, then test again.',
    };
  }
  if (isPermissionRefusal(err)) {
    return {
      kind: 'permission_denied',
      message: refusalMessage(err, 'Your RudderStack permission could not be checked.'),
      nextStep: 'Check your RudderStack access in Settings > Warehouses.',
    };
  }
  const code = serverCode(err);
  const failed = { kind: 'unknown' as const, message: "Lookout couldn't run the test.", ...detailOf(err) };
  if (code === 'BAD_REQUEST') {
    // Lookout's own validation of the request ("No connection string provided"): it says what to fix.
    return {
      kind: 'invalid_config',
      message: "Some connection details aren't valid.",
      nextStep: 'Fix them, then test again.',
      ...detailOf(err),
    };
  }
  if (code !== undefined && ACCESS_CODES.has(code)) return { ...failed, nextStep: ACCESS_STEP };
  if (isNetworkFailure(err)) return { ...failed, nextStep: 'Check your network connection, then try again.' };
  // Any other answer (a 500, an unreadable proxy error) says nothing about the details typed.
  return { ...failed, nextStep: 'Try again in a moment.' };
}

const LIST_MESSAGE = "Lookout couldn't load your RudderStack warehouse accounts.";

/** Reasons that mean the RudderStack link is broken: waiting will not help, reconnecting will. */
const LINK_INVALID_REASONS: ReadonlySet<string> = new Set(['unauthorized', 'workspace_not_linked']);

/**
 * The list loaded but RudderStack could not give its accounts, from a server that names only the
 * legacy `unavailable` reason and no `unavailableProblem` (an older server during a rolling deploy).
 * A broken link gets the words the server's `accountListProblem` gives it (the
 * `rudderstack_link_invalid` copy in `src/lib/warehouse-failure/copy.ts`); any other reason gets the
 * generic list copy. Retire this once no server sends `unavailable` alone.
 */
export function listUnavailable(reason: string): WarehouseProblem {
  if (LINK_INVALID_REASONS.has(reason)) {
    return {
      kind: 'rudderstack_link_invalid',
      code: reason,
      message: "Lookout's link to your RudderStack workspace needs to be renewed.",
      nextStep: 'Reconnect RudderStack in Settings, then try again.',
    };
  }
  return { kind: 'platform_unavailable', message: LIST_MESSAGE, nextStep: 'Wait a minute, then try again.' };
}

/** A list of RudderStack warehouse accounts that failed to load, where nothing was tested. */
export function listRequestFailed(err: unknown): WarehouseProblem {
  const fromServer = serverProblem(err);
  if (fromServer) return fromServer;
  const message = LIST_MESSAGE;
  const code = serverCode(err);
  if (code !== undefined && ACCESS_CODES.has(code)) {
    return { kind: 'permission_denied', message, nextStep: ACCESS_STEP, ...detailOf(err) };
  }
  if (isNetworkFailure(err)) {
    return {
      kind: 'network_unreachable',
      message,
      nextStep: 'Check your network connection, then try again.',
      ...detailOf(err),
    };
  }
  return { kind: 'platform_unavailable', message, nextStep: 'Wait a minute, then try again.', ...detailOf(err) };
}
